import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpErrorResponse } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { Observable, defer, finalize, firstValueFrom, of, shareReplay, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { LocalCacheService } from './local-cache.service';
import { SKIP_AUTH, SKIP_RETRY } from '../interceptors/http-context-tokens';
import { logout } from '../../store/auth/auth.actions';
import { environment } from '../../../environments/environment';

/**
 * Error codes UserService returns from POST auth/refresh-token when the refresh token can never work again
 * (RefreshTokenService.cs). RefreshToken_404 arrives as 404; RefreshToken_Expired and RefreshToken_Revoked arrive
 * as 500, so a status check alone does not recognise them. Same list as SMRTScape3.Web refresh-token-errors.ts.
 */
const DEAD_REFRESH_TOKEN_CODES = ['RefreshToken_404', 'RefreshToken_Expired', 'RefreshToken_Revoked'];

interface RefreshResponse {
  success?: boolean;
  data?: { token?: string; refreshToken?: string } | null;
}

/** True when the refresh call failed because the stored refresh token is dead, so the session is over. */
export function isDeadRefreshTokenError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  if (error.status === 400 || error.status === 401) return true;
  const errors = error.error?.errors;
  return Array.isArray(errors) && errors.some((code) => DEAD_REFRESH_TOKEN_CODES.includes(code));
}

/**
 * Gets a new access token with Console's own refresh token (Task 25762).
 *
 * Single flight: every caller that asks while a refresh is running shares that one call, so three parallel 401s
 * send one POST. Across browser tabs a Web Lock serialises the call, and a tab that finds the token already rotated
 * by another tab reuses the new tokens instead of sending the old one (which UserService would reject as revoked).
 *
 * Only a dead refresh token (400, 401 or one of the codes above) ends the session. A network error or another 5xx
 * is passed back to the caller and the session is kept, so a short outage does not sign the admin out.
 */
@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private http = inject(HttpClient);
  private cache = inject(LocalCacheService);
  private store = inject(Store);
  private toastr = inject(ToastrService);

  private readonly refreshUrl = `${environment.authApiOrigin}/refresh-token`;
  private readonly lockName = 'toro-oasis-console-refresh-token';
  private inFlight$: Observable<string> | null = null;

  /** Emits the new access token once, or errors. Never logs a token. */
  refresh(): Observable<string> {
    if (this.inFlight$) {
      return this.inFlight$;
    }

    const startedWith = this.cache.refreshToken;
    if (!startedWith) {
      return throwError(() => new Error('No refresh token'));
    }

    this.inFlight$ = this.underTabLock(startedWith).pipe(
      finalize(() => {
        this.inFlight$ = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight$;
  }

  private underTabLock(startedWith: string): Observable<string> {
    const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
    if (!locks) {
      return this.post(startedWith);
    }

    return defer(
      async (): Promise<string> =>
        await locks.request(this.lockName, async () => {
          const current = this.cache.refreshToken;
          if (!current) {
            // Another tab signed out while this one waited for the lock.
            throw new Error('No refresh token');
          }
          if (current !== startedWith && this.cache.authToken) {
            // Another tab rotated the tokens while this one waited. Use them.
            return this.cache.authToken;
          }
          return firstValueFrom(this.post(current));
        }),
    );
  }

  private post(refreshToken: string): Observable<string> {
    const body = { refreshToken, deviceIdentifier: this.cache.deviceIdentifier };
    const context = new HttpContext().set(SKIP_AUTH, true).set(SKIP_RETRY, true);

    return this.http.post<RefreshResponse>(this.refreshUrl, body, { context }).pipe(
      map((res) => {
        const token = res?.data?.token;
        const newRefreshToken = res?.data?.refreshToken;
        if (!res?.success || !token || !newRefreshToken) {
          throw new Error('Token refresh returned no token');
        }
        this.cache.authToken = token;
        this.cache.refreshToken = newRefreshToken;
        return token;
      }),
      catchError((err) => {
        if (isDeadRefreshTokenError(err)) {
          // A tab without Web Locks may have rotated the token in the meantime; its tokens are still good.
          const latest = this.cache.refreshToken;
          if (latest && latest !== refreshToken && this.cache.authToken) {
            return of(this.cache.authToken);
          }
          this.endSession();
        }
        return throwError(() => err);
      }),
    );
  }

  private endSession(): void {
    this.toastr.warning('Your session has expired. Please sign in again.', 'Signed out');
    // AuthEffects.logout$ clears the cache and goes to /sign-in.
    this.store.dispatch(logout());
  }
}
